import React, { useState } from 'react';
import { Star, Heart, Trash2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { motion } from 'framer-motion';

interface HigherPowerFormProps {
  onComplete: () => void;
  onClose: () => void;
  onDelete?: () => void;
  existingData?: {
    name: string;
    message: string;
    emoji: string;
  };
}

const EMOJI_CATEGORIES = [
  {
    name: "Nature",
    emojis: ['🌳', '🌊', '🌎', '🌞', '🌙', '⭐', '🌈', '🌺', '🍃', '🌿', '🌸', '🌄']
  },
  {
    name: "Spiritual",
    emojis: ['✨', '🕊️', '🙏', '📿', '☮️', '☯️', '🕉️', '☪️', '✝️', '✡️', '☸️', '🪬']
  },
  {
    name: "Animals",
    emojis: ['🦋', '🦁', '🦅', '🐺', '🦊', '🐘', '🐬', '🦉', '🐢', '🦌', '🐎', '🐋']
  },
  {
    name: "Divine",
    emojis: ['👼', '💫', '🌟', '💖', '💝', '💫', '🔮', '⚡', '🕯️', '🪷', '🎭', '🎋']
  }
];

export default function HigherPowerForm({ onComplete, onClose, onDelete, existingData }: HigherPowerFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: existingData?.name || '',
    message: existingData?.message || '',
    emoji: existingData?.emoji || '⭐'
  });
  const [selectedCategory, setSelectedCategory] = useState('Nature');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          higher_power_enabled: true,
          higher_power_name: formData.name,
          higher_power_message: formData.message,
          higher_power_emoji: formData.emoji
        })
        .eq('id', user.id);

      if (updateError) throw updateError;
      onComplete();
    } catch (err) {
      console.error('Error saving higher power:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !onDelete) return;

    if (!confirm('Are you sure you want to remove your Higher Power? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          higher_power_enabled: false,
          higher_power_name: null,
          higher_power_message: null,
          higher_power_emoji: '⭐'
        })
        .eq('id', user.id);

      if (updateError) throw updateError;
      onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting higher power:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-midnight-900/90 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col relative"
      >
        {/* Header - Fixed */}
        <div className="p-6 border-b border-mint-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-amber-400 to-amber-500 rounded-lg shadow-lg">
                <Star className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-midnight-800">
                {existingData ? 'Edit Higher Power' : 'Connect to Your Higher Power'}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              {onDelete && (
                <button
                  onClick={handleDelete}
                  className="p-2 text-bubblegum-600 hover:bg-bubblegum-50 rounded-lg transition-colors"
                  title="Remove Higher Power"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-midnight-400 hover:text-midnight-600 hover:bg-midnight-50 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <p className="text-midnight-600">
            Connect with something greater than yourself - a source of wisdom, strength, and comfort that can support you on your healing journey.
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-bubblegum-50 text-bubblegum-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-midnight-700">
                Name Your Higher Power
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="e.g., Divine Love, The Universe, Great Spirit, Inner Wisdom"
                required
              />
              <p className="text-sm text-midnight-500">
                Choose a name that resonates with your personal understanding of the sacred.
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-midnight-700">
                Guiding Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                className="w-full px-4 py-2 border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                rows={4}
                placeholder="What message from your Higher Power brings you comfort and strength?"
                required
              />
              <p className="text-sm text-midnight-500">
                This message will appear on your Higher Power card as a reminder of your connection.
              </p>
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-midnight-700">
                Choose a Symbol
              </label>
              
              {/* Category Tabs */}
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {EMOJI_CATEGORIES.map(category => (
                  <button
                    key={category.name}
                    type="button"
                    onClick={() => setSelectedCategory(category.name)}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
                      ${selectedCategory === category.name
                        ? 'bg-amber-500 text-white'
                        : 'bg-mint-100 text-midnight-600 hover:bg-mint-200'
                      }
                    `}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {/* Emoji Grid */}
              <div className="grid grid-cols-6 gap-2">
                {EMOJI_CATEGORIES.find(c => c.name === selectedCategory)?.emojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, emoji }))}
                    className={`
                      p-4 text-2xl rounded-lg border-2 transition-all hover:scale-105
                      ${formData.emoji === emoji
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-mint-200 hover:border-mint-300'
                      }
                    `}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </form>
        </div>

        {/* Footer - Fixed */}
        <div className="p-6 border-t border-mint-200 bg-mint-50 flex-shrink-0">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-midnight-600 hover:text-midnight-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all disabled:opacity-50 flex items-center space-x-2"
            >
              <Heart className="h-5 w-5" />
              <span>{loading ? 'Saving...' : existingData ? 'Update' : 'Connect'}</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}